import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import React, { useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { selectTeamPagination, setTeamPagination } from '../../model/teamSlice'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { ProjectListRow, useGetTeamManagementListQuery } from '../../../../app/api'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { InviteModal } from '../invite-modal/InviteModal'
import { useTranslation } from 'react-i18next'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { StatusCell } from '../../ui/cells/StatusCell'
import { RoleCell } from '../../ui/cells/RoleCell'
import { ProjectsCell } from '../../ui/cells/ProjectsCell'
import { ActionCell } from '../../ui/cells/ActionCell'
import styles from './TeamTable.module.scss'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'

export const TeamTable = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const { current, pageSize, total } = useAppSelector<IPagination>(selectTeamPagination)
  const [queryParams, setQueryParams] = useState<any>({
    page: current,
    'per-page': pageSize,
  })
  const dispatch = useAppDispatch()

  const { data, isLoading, isFetching } = useGetTeamManagementListQuery(queryParams)

  const columns: ColumnsType<any> = [
    {
      title: t('table.columns.username'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('table.columns.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: () => (
        <span className={'item-center flex gap-[8px]'}>
          {t('table.columns.status')}
          <BpTooltip
            title={
              <ul className={'list-disc pl-[12px]'}>
                <li>{t('pages.team.statusTooltip.active')}</li>
                <li>{t('pages.team.statusTooltip.pending')}</li>
                <li>{t('pages.team.statusTooltip.deactivated')}</li>
              </ul>
            }
            className={'!max-w-[550px]'}
            style={{ maxWidth: 500 }}
          />
        </span>
      ),
      key: 'status',
      render: (_: string, record) => <StatusCell {...record} />,
    },
    {
      title: t('table.columns.role'),
      dataIndex: 'sub_role',
      key: 'sub_role',
      render: (_: string, record) => <RoleCell {...record} />,
    },
    {
      title: t('table.columns.projects'),
      key: 'projects',
      render: (_: string, record) => <ProjectsCell {...record} />,
    },
    {
      title: t('table.columns.lastVisit'),
      dataIndex: 'last_visit',
      key: 'last_visit',
      width: 150,
    },
    {
      key: 'actions',
      render: (_: string, record) => <ActionCell {...record} />,
      width: 64,
      fixed: 'right',
    },
  ]

  const handleTableChange = (
    { pageSize, current, total }: IBpPaginationProps,
    sorter?: SorterResult<any> | SorterResult<any>[],
  ) => {
    dispatch(
      setTeamPagination({
        pageSize: pageSize as number,
        current: current as number,
        total: total as number,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: current,
      'per-page': pageSize,
      sort: useSorter(sorter),
    })
  }

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <BpButton
          contentClassName={styles.inviteButton}
          iconLeft={<IconPlus />}
          size={'sm'}
          onClick={() => dispatch(setModal({ children: <InviteModal /> }))}
        >
          {t('pages.team.inviteTeamMember')}
        </BpButton>
        {
          {
            bluepear: (
              <AiButton
                unstyled
                onClick={() =>
                  handleSendMessage({
                    message:
                      'Please provide a clear guide on how to manage users in the platform—covering roles and permissions.',
                  })
                }
              >
                How it works
              </AiButton>
            ),
            robuscan: null,
            default: null,
          }[process.env.REACT_APP_PROJECT ?? 'default']
        }
      </div>
      <BpTable
        pagination={{
          current,
          pageSize,
          total,
        }}
        columns={columns}
        data={data as TTableResponse<ProjectListRow>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={(record) => record.id}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}
